import { Map as ImmutableMap } from 'immutable';
import { validateCondition } from '../utils/conditionValidation';
import { getIntegrationAppId } from '../utils/getIntegrationAppId';
import invariant from 'react-utils/invariant';
import { getDSFieldRecordsFromPropertyGroupRecords, isMapOfDSFieldRecords, isMapOfPropertyGroupRecords } from '../utils/strategyBaseUtils';
const isCustomFilterFamilySubGroup = thing => {
  if ('name' in thing && 'options' in thing) {
    return true;
  }
  return false;
};
const isCustomFilterFamily = thing => {
  if ('value' in thing) {
    return true;
  }
  return false;
};
const convertCustomFilterFamilyToOption = (customFilterFamily, filterFamilyName) => {
  const option = {
    text: filterFamilyName,
    value: customFilterFamily.value,
    isAssociation: customFilterFamily.isAssociation
  };
  return option;
};
const convertCustomFilterFamilySubGroupToOption = entry => {
  return {
    text: entry.text,
    value: entry.name,
    isCategoriesGroup: true
  };
};
export class StrategyBase {
  constructor(strategyDefinition) {
    this.customConfig = {};
    this.onMount = () => Promise.resolve();
    this.onEditorMount = () => Promise.resolve();
    this.onUpdate = () => undefined;
    this.getRefinementFieldGroups = () => undefined;
    this.getRefinementFields = () => undefined;
    this.getFieldDefinitions = () => undefined;
    this.getCustomFieldsForAssetFamily = () => ImmutableMap();
    this.getHasFilterFamilyAccess = () => true;
    this.getOperatorsForType = () => undefined;
    this.getReferenceResolvers = () => ImmutableMap();
    this.getSpecialFieldOptions = () => ({});
    this.getObjectAssociationOptions = () => [];
    this.validateOperator = validateCondition;
    this.getIsFiscalYearEnabled = () => false;
    this.getIsRollingDateOffsetInputEnabled = () => false;
    this.getRequiredFields = () => new Set();
    this.getDefaultTimezone = () => undefined;
    this.getTimezoneOptions = () => [];
    this.isNotCompletedUnifiedEventFiltersEnabled = true;
    this.isIncludeObjectsWithNoValueSetCheckboxEnabled = false;
    this.isSameObjectAssociationFilteringEnabled = false;
    this.isTestFiltersEnabled = false;
    this.useHoursFiltering = false;
    this.canEditLogicGroupCondition = false;
    this.getFilterFamilyOptions_original = () => [];
    this.getGroupedFilterFamilyOptions_original = () => [];
    this.getFilterFamilyName_original = () => '';
    this.getFilterFamilyGroupHeading_original = () => undefined;
    this.getFilterFamilyObjectName_original = () => '';
    this.getField_original = () => undefined;
    this.getFields_original = () => undefined;
    this.getFieldGroups_original = () => undefined;
    this.getCustomConfig = () => {
      return this.customConfig;
    };
    this.populateFilterCategoryGroup = (props, filterFamilyOption, selectedObjectType) => {
      const {
        text,
        value,
        isAssociation
      } = filterFamilyOption;
      if (filterFamilyOption.isCategoriesGroup) {
        const groupedFilterFamilyOptions = this.getGroupedFilterFamilyOptions_original(props, value, selectedObjectType, {
          integrationAppId: getIntegrationAppId(value)
        })[0];
        invariant(groupedFilterFamilyOptions, `Grouped filter family options for ${value} does not exist.`);
        return {
          options: groupedFilterFamilyOptions.options,
          name: value,
          text
        };
      }
      return {
        text,
        value,
        isAssociation: !!isAssociation
      };
    };
    this.populateNestedFilterCategoryGroups = (props, selectedObjectType, filterFamilyOptions) => {
      return filterFamilyOptions.map(({
        category,
        text,
        options
      }) => {
        return {
          category,
          text,
          options: options.map(option => this.populateFilterCategoryGroup(props, option, selectedObjectType))
        };
      });
    };
    this.findEntryInOptions = (value, options) => {
      for (const entry of options) {
        if ('name' in entry && value === entry.name) {
          return entry;
        }
        if ('value' in entry && value === entry.value) {
          return entry;
        }
        if ('options' in entry) {
          const maybeEntry = this.findEntryInOptions(value, entry.options);
          if (maybeEntry) {
            return maybeEntry;
          }
        }
      }
      return undefined;
    };
    this.findCustomFilterFamilyEntry = (selectedObjectType, value, isSameObjectAssociationFilteringEnabled, props) => {
      if (typeof this.customConfig.getFilterFamilies !== 'function') {
        return undefined;
      }
      const defaultFilterFamilies = this.populateNestedFilterCategoryGroups(props, selectedObjectType, this.getFilterFamilyOptions_original(props, selectedObjectType, isSameObjectAssociationFilteringEnabled));
      const filterFamilyGroups = this.customConfig.getFilterFamilies(selectedObjectType.filterFamily, !selectedObjectType.isObjectOwnFilterBranch, defaultFilterFamilies);
      return this.findEntryInOptions(value, filterFamilyGroups);
    };
    this.findCustomFilterFamily = (selectedObjectType, value, isSameObjectAssociationFilteringEnabled, props) => {
      const entry = this.findCustomFilterFamilyEntry(selectedObjectType, value, isSameObjectAssociationFilteringEnabled, props);
      if (entry && isCustomFilterFamily(entry)) {
        return entry;
      }
      return undefined;
    };
    this.findCustomFilterFamilySubGroup = (selectedObjectType, value, isSameObjectAssociationFilteringEnabled, props) => {
      const entry = this.findCustomFilterFamilyEntry(selectedObjectType, value, isSameObjectAssociationFilteringEnabled, props);
      if (entry && isCustomFilterFamilySubGroup(entry)) {
        return entry;
      }
      return undefined;
    };
    this.strategyDefinition = strategyDefinition;
    this.onMount = this.pickProperty('onMount', strategyDefinition);
    this.onEditorMount = this.pickProperty('onEditorMount', strategyDefinition);
    this.onUpdate = this.pickProperty('onUpdate', strategyDefinition);
    this.getRefinementFieldGroups = this.pickProperty('getRefinementFieldGroups', strategyDefinition);
    this.getRefinementFields = this.pickProperty('getRefinementFields', strategyDefinition);
    this.getRequiredFields = this.pickProperty('getRequiredFields', strategyDefinition);
    this.getFieldDefinitions = this.pickProperty('getFieldDefinitions', strategyDefinition);
    this.getCustomFieldsForAssetFamily = this.pickProperty('getCustomFieldsForAssetFamily', strategyDefinition);
    this.getHasFilterFamilyAccess = this.pickProperty('getHasFilterFamilyAccess', strategyDefinition);
    this.getOperatorsForType = this.pickProperty('getOperatorsForType', strategyDefinition);
    this.getReferenceResolvers = this.pickProperty('getReferenceResolvers', strategyDefinition);
    this.getSpecialFieldOptions = this.pickProperty('getSpecialFieldOptions', strategyDefinition);
    this.getObjectAssociationOptions = this.pickProperty('getObjectAssociationOptions', strategyDefinition);
    this.serializer = this.pickProperty('serializer', strategyDefinition);
    this.wrapSingleFilter = this.pickProperty('wrapSingleFilter', strategyDefinition);
    this.unwrapSingleFilter = this.pickProperty('unwrapSingleFilter', strategyDefinition);
    this.validateOperator = this.pickProperty('validateOperator', strategyDefinition);
    this.evaluateFiltersForObject = this.pickProperty('evaluateFiltersForObject', strategyDefinition);
    this.getIsFiscalYearEnabled = this.pickProperty('getIsFiscalYearEnabled', strategyDefinition);
    this.getIsRollingDateOffsetInputEnabled = this.pickProperty('getIsRollingDateOffsetInputEnabled', strategyDefinition);
    this.getDefaultTimezone = this.pickProperty('getDefaultTimezone', strategyDefinition);
    this.getTimezoneOptions = this.pickProperty('getTimezoneOptions', strategyDefinition);
    this.conditionsPerORGroupLimit = this.pickProperty('conditionsPerORGroupLimit', strategyDefinition);
    this.isNotCompletedUnifiedEventFiltersEnabled = this.pickProperty('isNotCompletedUnifiedEventFiltersEnabled', strategyDefinition);
    this.isIncludeObjectsWithNoValueSetCheckboxEnabled = this.pickProperty('isIncludeObjectsWithNoValueSetCheckboxEnabled', strategyDefinition);
    this.isSameObjectAssociationFilteringEnabled = this.pickProperty('isSameObjectAssociationFilteringEnabled', strategyDefinition);
    this.isTestFiltersEnabled = this.pickProperty('isTestFiltersEnabled', strategyDefinition);
    this.useHoursFiltering = this.pickProperty('useHoursFiltering', strategyDefinition);
    this.strategyType = this.pickProperty('strategyType', strategyDefinition);
    this.canEditLogicGroupCondition = this.pickProperty('canEditLogicGroupCondition', strategyDefinition);

    // customizable methods
    this.getFilterFamilyOptions_original = strategyDefinition.getFilterFamilyOptions;
    this.getGroupedFilterFamilyOptions_original = strategyDefinition.getGroupedFilterFamilyOptions || this.getGroupedFilterFamilyOptions_original;
    this.getFilterFamilyName_original = strategyDefinition.getFilterFamilyName;
    this.getFilterFamilyGroupHeading_original = strategyDefinition.getFilterFamilyGroupHeading;
    this.getFilterFamilyObjectName_original = strategyDefinition.getFilterFamilyObjectName;
    this.getField_original = strategyDefinition.getField;
    this.getFields_original = strategyDefinition.getFields || this.getFields_original;
    this.getFieldGroups_original = strategyDefinition.getFieldGroups || this.getFieldGroups_original;
  }
  pickProperty(key, source) {
    if (key in source) {
      return source[key];
    }
    return this[key];
  }
  applyCustomConfig(customConfig) {
    if (typeof customConfig.isSameObjectAssociationFilteringEnabled === 'boolean') {
      this.isSameObjectAssociationFilteringEnabled = customConfig.isSameObjectAssociationFilteringEnabled;
    }
    if (typeof customConfig.isTestFiltersEnabled === 'boolean') {
      this.isTestFiltersEnabled = customConfig.isTestFiltersEnabled;
    }
    if (typeof customConfig.isNotCompletedUnifiedEventFiltersEnabled === 'boolean') {
      this.isNotCompletedUnifiedEventFiltersEnabled = customConfig.isNotCompletedUnifiedEventFiltersEnabled;
    }
    if (typeof customConfig.useHoursFiltering === 'boolean') {
      this.useHoursFiltering = customConfig.useHoursFiltering;
    }
    if (typeof customConfig.conditionsPerORGroupLimit === 'number') {
      this.conditionsPerORGroupLimit = customConfig.conditionsPerORGroupLimit;
    }
    if (typeof customConfig.evaluateFiltersForObject === 'function') {
      this.evaluateFiltersForObject = customConfig.evaluateFiltersForObject;
    }
    this.customConfig = customConfig;
  }
  getFilterFamilyOptions(props, selectedObjectType, isSameObjectAssociationFilteringEnabled) {
    if (typeof this.customConfig.getFilterFamilies !== 'function') {
      return this.getFilterFamilyOptions_original(props, selectedObjectType, isSameObjectAssociationFilteringEnabled);
    }
    const defaultFilterFamilies = this.populateNestedFilterCategoryGroups(props, selectedObjectType, this.getFilterFamilyOptions_original(props, selectedObjectType, isSameObjectAssociationFilteringEnabled));
    const filterFamilyGroups = this.customConfig.getFilterFamilies(selectedObjectType.filterFamily, !selectedObjectType.isObjectOwnFilterBranch, defaultFilterFamilies);
    return filterFamilyGroups.map(group => {
      return {
        category: group.category,
        text: group.text,
        options: group.options.map(entry => {
          if (isCustomFilterFamilySubGroup(entry)) {
            return convertCustomFilterFamilySubGroupToOption(entry);
          }
          return convertCustomFilterFamilyToOption(entry, this.getFilterFamilyName(entry.value, props, selectedObjectType, isSameObjectAssociationFilteringEnabled));
        })
      };
    });
  }
  getGroupedFilterFamilyOptions(props, groupName, selectedObjectType, isSameObjectAssociationFilteringEnabled, options) {
    if (typeof this.customConfig.getFilterFamilies !== 'function') {
      return this.getGroupedFilterFamilyOptions_original(props, groupName, selectedObjectType, options);
    }
    const group = this.findCustomFilterFamilySubGroup(selectedObjectType, groupName, isSameObjectAssociationFilteringEnabled, props);
    if (group) {
      return [{
        text: group.text,
        options: group.options.map(entry => {
          if (isCustomFilterFamilySubGroup(entry)) {
            return convertCustomFilterFamilySubGroupToOption(entry);
          }
          return convertCustomFilterFamilyToOption(entry, this.getFilterFamilyName(entry.value, props, selectedObjectType, isSameObjectAssociationFilteringEnabled));
        })
      }];
    }
    return [];
  }
  getFilterFamilyName(filterFamily, props, selectedObjectType, isSameObjectAssociationFilteringEnabled, isSameObjectAssociation = false) {
    const filterFamilyName = this.getFilterFamilyName_original(filterFamily, props, isSameObjectAssociation);
    const customFilterFamily = this.findCustomFilterFamily(selectedObjectType, filterFamily, isSameObjectAssociationFilteringEnabled, props);
    if (!customFilterFamily) {
      return filterFamilyName;
    }
    return customFilterFamily.text || filterFamilyName;
  }
  getFilterFamilyGroupHeading(filterFamily, associationId, isEditor, props, selectedObjectType, isSameObjectAssociationFilteringEnabled, isSameObjectAssociation = false) {
    const groupHeading = this.getFilterFamilyGroupHeading_original(filterFamily, associationId, isEditor, props, isSameObjectAssociation);
    const customFilterFamily = this.findCustomFilterFamily(selectedObjectType, filterFamily, isSameObjectAssociationFilteringEnabled, props);
    if (!customFilterFamily) {
      return groupHeading;
    }
    return customFilterFamily.groupHeading || groupHeading;
  }
  getFilterFamilyObjectName(filterFamily, selectedObjectType, isSameObjectAssociationFilteringEnabled, props, isPlural, data) {
    const groupHeading = this.getFilterFamilyObjectName_original(filterFamily, isPlural, data);
    const customFilterFamily = this.findCustomFilterFamily(selectedObjectType, filterFamily, isSameObjectAssociationFilteringEnabled, props);
    if (!customFilterFamily) {
      return groupHeading;
    }
    return customFilterFamily.entityName ? String(customFilterFamily.entityName) : groupHeading;
  }
  getField(filterFamily, id, fallbackField, props) {
    const defaultField = this.getField_original(filterFamily, id, fallbackField, props);
    if (typeof this.customConfig.getField === 'function') {
      return this.customConfig.getField(filterFamily, id, defaultField);
    }
    return defaultField;
  }
  getFields(filterFamily, props, includeAssociations = false) {
    const defaultFields = this.getFields_original(filterFamily, props, includeAssociations);
    if (typeof this.customConfig.getFields === 'function') {
      const defaultFieldGroups = this.getFieldGroups_original(filterFamily, props);
      // original fields can be either PropertyGroupRecords or DSFieldRecords
      // we prioritize PropertyGroupRecords for field selection panel in editor
      const originalFields = defaultFieldGroups && defaultFieldGroups.size > 0 ? defaultFieldGroups : defaultFields;
      const fields = this.customConfig.getFields(filterFamily, originalFields, {
        isAssociation: !!props.isAssociation
      });
      if (isMapOfDSFieldRecords(fields)) {
        return fields;
      }
      if (isMapOfPropertyGroupRecords(fields)) {
        return getDSFieldRecordsFromPropertyGroupRecords(fields);
      }
      return undefined;
    }
    return defaultFields;
  }
  getFieldGroups(filterFamily, props) {
    const defaultFieldGroups = this.getFieldGroups_original(filterFamily, props);
    if (typeof this.customConfig.getFields === 'function') {
      // original fields can be either PropertyGroupRecords or DSFieldRecords
      // we prioritize PropertyGroupRecords for field selection panel in editor
      const originalFields = defaultFieldGroups && defaultFieldGroups.size > 0 ? defaultFieldGroups : this.getFields_original(filterFamily, props);
      const fields = this.customConfig.getFields(filterFamily, originalFields, {
        isAssociation: !!props.isAssociation
      });
      // custom `getFields` can return map of PropertyGroupRecords or DSFieldRecords
      // this check ensures we only return PropertyGroupRecords
      if (isMapOfPropertyGroupRecords(fields)) {
        return fields;
      }
      return undefined;
    }
    return defaultFieldGroups;
  }
}